<template>
  <div class="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Top Navigation -->
    <nav class="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-40">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex items-center gap-3">
            <i class="pi pi-briefcase text-primary-600 text-2xl"></i>
            <span class="text-xl font-bold text-gray-900 dark:text-white">{{ t('app.portalTitle') }}</span>
          </div>

          <div class="flex items-center gap-2 sm:gap-3">
            <div ref="notifRoot" class="relative" data-portal-notifications>
              <button
                type="button"
                class="p-2.5 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition touch-manipulation relative"
                :aria-label="t('common.notifications')"
                @click.stop="toggleNotifications"
              >
                <i class="pi pi-bell text-xl text-gray-600 dark:text-gray-300" />
                <span
                  v-if="unreadNotifications.length > 0"
                  class="absolute top-1 right-1 min-w-[1.125rem] h-[1.125rem] px-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                >
                  {{ unreadNotifications.length > 9 ? '9+' : unreadNotifications.length }}
                </span>
              </button>
              <div
                v-if="notificationsOpen"
                class="absolute right-0 mt-2 w-[min(100vw-2rem,20rem)] max-h-80 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl z-50"
                @click.stop
              >
                <div class="px-3 py-2 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-900 dark:text-white">
                  {{ t('common.notifications') }}
                </div>
                <div v-if="notificationsLoading" class="p-4 text-sm text-gray-500 text-center">{{ t('common.loading') }}</div>
                <div v-else-if="unreadNotifications.length === 0" class="p-4 text-sm text-gray-500 text-center">
                  {{ t('messages.noUnreadNotifications') }}
                </div>
                <ul v-else class="divide-y divide-gray-100 dark:divide-gray-700">
                  <li
                    v-for="n in unreadNotifications"
                    :key="n.id"
                    class="px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ n.title }}</p>
                    <p class="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-snug">{{ n.message }}</p>
                  </li>
                </ul>
              </div>
            </div>

            <div class="hidden sm:flex items-center gap-2 min-w-0 pr-1">
              <Avatar
                :label="userInitials"
                :style="{ backgroundColor: '#488BE6', color: '#fff' }"
                shape="circle"
                class="shrink-0"
              />
              <span class="text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-w-[100px] sm:max-w-[160px]">
                {{ displayName }}
              </span>
            </div>

            <!-- Dil seçici (yalnızca çok dilli dağıtımda görünür) -->
            <LanguageSwitcher />

            <button
              type="button"
              @click="toggleTheme"
              class="p-2.5 min-w-[44px] min-h-[44px] inline-flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition touch-manipulation"
              :title="theme === 'light' ? t('common.darkTheme') : t('common.lightTheme')"
              :aria-label="theme === 'light' ? t('common.darkTheme') : t('common.lightTheme')"
            >
              <i :class="theme === 'light' ? 'pi pi-moon' : 'pi pi-sun'" class="text-xl"></i>
            </button>

            <button
              type="button"
              @click="logout"
              class="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] rounded-xl font-semibold text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border-2 border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600 active:scale-[0.98] transition touch-manipulation shadow-sm"
              :aria-label="t('common.logout')"
            >
              <i class="pi pi-sign-out text-lg" aria-hidden="true" />
              <span>{{ t('common.logoutShort') }}</span>
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- Masaüstü / tablet: üst menü (mobilde alt menü) -->
    <PortalNavStrip />

    <!-- Main Content — mobilde alt menü için boşluk -->
    <main class="flex-1 overflow-auto pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-8">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4">
        <slot></slot>
      </div>
    </main>

    <!-- Mobil: parmakla erişim -->
    <PortalBottomNav class="md:hidden" />

    <!-- Doğum günü / iş yıl dönümü görseli (o gün ilk açılışta, bir kez) -->
    <CelebrationModal />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Avatar from 'primevue/avatar'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { useFormatters } from '@/composables/useFormatters'
import { useAuth } from '@/composables/useAuth'
import PortalBottomNav from '@/components/common/PortalBottomNav.vue'
import PortalNavStrip from '@/components/common/PortalNavStrip.vue'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import CelebrationModal from '@/components/common/CelebrationModal.vue'
import { notificationsService, type AppNotification } from '@/services/notifications.service'

const { t } = useI18n()
const authStore = useAuthStore()
const uiStore = useUIStore()
const { getInitials } = useFormatters()
const { logout } = useAuth()

const theme = computed(() => uiStore.theme)
const userInitials = computed(() => {
  if (authStore.user) {
    return getInitials(authStore.user.firstName, authStore.user.lastName)
  }
  return 'U'
})

const displayName = computed(() => {
  if (!authStore.user) return ''
  const u = authStore.user
  return `${u.firstName} ${u.lastName}`.trim() || u.email || t('common.employee')
})

const notifRoot = ref<HTMLElement | null>(null)
const notificationsOpen = ref(false)
const notificationsLoading = ref(false)
const unreadNotifications = ref<AppNotification[]>([])

async function loadUnreadNotifications() {
  notificationsLoading.value = true
  try {
    unreadNotifications.value = await notificationsService.getUnread()
  } catch {
    unreadNotifications.value = []
  } finally {
    notificationsLoading.value = false
  }
}

function toggleNotifications() {
  notificationsOpen.value = !notificationsOpen.value
  if (notificationsOpen.value) {
    loadUnreadNotifications()
  }
}

function onDocClick(e: MouseEvent) {
  const root = notifRoot.value
  if (!root || root.contains(e.target as Node)) return
  notificationsOpen.value = false
}

function onRefreshEvent() {
  loadUnreadNotifications()
}

onMounted(() => {
  loadUnreadNotifications()
  window.addEventListener('portal-notifications-refresh', onRefreshEvent)
  document.addEventListener('click', onDocClick)
})

onUnmounted(() => {
  window.removeEventListener('portal-notifications-refresh', onRefreshEvent)
  document.removeEventListener('click', onDocClick)
})

function toggleTheme() {
  uiStore.toggleTheme()
}
</script>
